import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { layersApi, type FeatureHierarchy, type FeatureTreeNode } from '../../lib/layersApi';

function TreeNode({ node }: { node: FeatureTreeNode }) {
  return (
    <li className="relative">
      <div className="flex items-center gap-2 py-1 pl-4">
        <span className="absolute left-0 top-[15px] h-px w-3.5 bg-border-default" />
        <Link
          to={`/app/data/layers/${node.layer_id}/objects/${node.id}`}
          className="truncate text-[13px] font-medium text-action-primary hover:underline"
        >
          {node.title}
        </Link>
        <span className="shrink-0 text-[11px] text-text-secondary">{node.layer_name}</span>
      </div>
      {node.children.length > 0 && (
        <ul className="ml-[7px] flex flex-col border-l border-border-default pl-0">
          {node.children.map((child) => (
            <TreeNode key={child.id} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** "Показать иерархию" — the parent/children shown inline on the object card
 * only ever surface one level; this walks the whole chain (root ancestor down
 * to every descendant, arbitrary depth) and lays it out as a simple indented
 * tree, ancestors as a staircase leading down to the highlighted object,
 * descendants as a connected list below it. */
export function HierarchyModal({
  layerId,
  featureId,
  onClose,
}: {
  layerId: string;
  featureId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<FeatureHierarchy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    layersApi
      .getFeatureHierarchy(layerId, featureId)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Не удалось загрузить иерархию.'))
      .finally(() => setLoading(false));
  }, [layerId, featureId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-6" onClick={onClose}>
      <div
        className="flex max-h-[80vh] w-full max-w-[560px] flex-col overflow-hidden rounded-2xl border border-border-default bg-bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border-default px-5 py-3.5">
          <h2 className="text-[15px] font-semibold text-text-primary">Иерархия объекта</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex size-8 items-center justify-center rounded-lg text-lg text-text-secondary hover:bg-bg-surface-secondary"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {loading && <p className="text-[13px] text-text-secondary">Загрузка…</p>}
          {error && <p className="text-[13px] font-medium text-status-danger">{error}</p>}
          {data && (
            <div className="flex flex-col">
              {data.ancestors.length === 0 && data.children.length === 0 && (
                <p className="mb-3 text-[12px] text-text-secondary">
                  У объекта нет ни родителя, ни дочерних объектов — иерархия из одного звена.
                </p>
              )}

              {data.ancestors.map((a, i) => (
                <div key={a.id} className="flex items-center gap-2 py-1" style={{ marginLeft: i * 20 }}>
                  <Link
                    to={`/app/data/layers/${a.layer_id}/objects/${a.id}`}
                    className="truncate text-[13px] font-medium text-action-primary hover:underline"
                  >
                    {a.title}
                  </Link>
                  <span className="shrink-0 text-[11px] text-text-secondary">{a.layer_name}</span>
                </div>
              ))}

              <div
                className="flex items-center gap-2 rounded-lg bg-action-primary/10 px-2.5 py-1.5"
                style={{ marginLeft: data.ancestors.length * 20 }}
              >
                <span className="truncate text-[13px] font-semibold text-text-primary">{data.target.title}</span>
                <span className="shrink-0 text-[11px] text-text-secondary">{data.target.layer_name}</span>
                <span className="shrink-0 text-[10px] font-medium text-action-primary">этот объект</span>
              </div>

              {data.children.length > 0 && (
                <ul
                  className="mt-1 flex flex-col border-l border-border-default pl-0"
                  style={{ marginLeft: data.ancestors.length * 20 + 7 }}
                >
                  {data.children.map((child) => (
                    <TreeNode key={child.id} node={child} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
